import { Routes, Route, Navigate, useLocation, useParams, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Suspense, lazy, useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { PREFIXED_LOCALES } from '@/utils/localePath'
import Navbar from '@/components/layout/Navbar'
import Footer from '@/components/layout/Footer'
import BackToTop from '@/components/ui/BackToTop'
import HomePage from '@/pages/HomePage'
import AboutPage from '@/pages/AboutPage'
import ProductsPage from '@/pages/ProductsPage'
import ContactPage from '@/pages/ContactPage'
import MagazinePage from '@/pages/MagazinePage'
import MagazineDetailPage from '@/pages/MagazineDetailPage'
import LecturesPage from '@/pages/LecturesPage'
import LectureDetailPage from '@/pages/LectureDetailPage'
import HarbourPage from '@/pages/HarbourPage'
import ValuesIncubatorPage from '@/pages/ValuesIncubatorPage'
import SmartCindyPage from '@/pages/SmartCindyPage'
import NationalEducationPage from '@/pages/NationalEducationPage'
import NationalEducationKs2Page from '@/pages/NationalEducationKs2Page'
import CspPage from '@/pages/CspPage'
import ProductDetailPage from '@/pages/ProductDetailPage'
import NotFoundPage from '@/pages/NotFoundPage'
import ArticleList from '@/pages/ArticleList'
import ArticleDetail from '@/pages/ArticleDetail'
import GalleryListPage from '@/pages/GalleryList'
import GalleryDetailPage from '@/pages/GalleryDetail'

/**
 * BareRedirect — `<Navigate>` wrapper that substitutes `:param` placeholders
 * in the target string from the current matched route's params.
 *
 * Why this exists: `<Navigate to="/zh-Hant/articles/:slug">` does NOT
 * interpolate `:slug` for you — that syntax is purely for incoming route
 * matching. Without this wrapper, navigating to `/articles/foo` matches the
 * bare `/articles/:slug` route, then `<Navigate>` pushes the literal URL
 * `/zh-Hant/articles/:slug`, where the `:slug` segment is plain text. The
 * localized route matches it, captures `:slug` as a param (the string
 * `:slug`), ArticleDetail renders with the wrong slug, and the API 404s
 * — sending the user straight back to the list page. Substituting the
 * captured param fixes all three bare-redirects (articles / magazine /
 * lectures).
 */
function BareRedirect({ to }: { to: string }) {
  const params = useParams()
  // Replace `:name` (any param captured by the matched route) with the value.
  // Param names follow the route pattern — they're word-char only per the
  // route syntax — so a simple regex is enough; no escaping needed for
  // route-defined names.
  const resolved = to.replace(/:([a-zA-Z][a-zA-Z0-9_]*)/g, (_, key: string) => {
    const v = params[key]
    // If the param wasn't matched, leave the placeholder in place — better
    // to land on a 404 than silently drop the user on a wrong page.
    return v !== undefined ? v : `:${key}`
  })
  return <Navigate to={resolved} replace />
}

/**
 * The admin is a separate bundle.
 *
 * It was statically imported, so every public visitor downloaded the whole CMS
 * — every editor, list screen, and the TinyMCE React wrapper — inside the main
 * chunk (that chunk was already 1MB before the drag-and-drop nav editor landed
 * on top of it). Operators are a tiny fraction of traffic and pay none of that.
 *
 * `Suspense` shows nothing rather than a spinner: the admin's own shell has a
 * loading state for its data, and a brief blank frame while the chunk arrives
 * reads better than a flash of public-site chrome.
 */
const AdminApp = lazy(() => import('@/admin/AdminApp'))

/**
 * PublicLayout — wraps every public route with Navbar + Footer + scroll animation.
 * Routes that need this chrome nest inside <Outlet />.
 */
function PublicLayout() {
  const { t, i18n } = useTranslation()
  const location = useLocation()

  // Sync <html lang> with i18next language
  useEffect(() => {
    document.documentElement.lang = i18n.language
  }, [i18n.language])

  // Scroll behaviour on navigation (same rules as before).
  useEffect(() => {
    const hash = location.hash?.slice(1)
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
      return
    }
    const id = window.requestAnimationFrame(() => {
      const el = document.getElementById(hash)
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
    return () => window.cancelAnimationFrame(id)
  }, [location.pathname, location.hash])

  return (
    <>
      <a href="#main-content" className="skip-link">
        {t('common.skipToContent')}
      </a>
      <Navbar />
      <AnimatePresence mode="wait" initial={false}>
        <motion.main
          key={location.pathname}
          id="main-content"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
        >
          <Outlet />
        </motion.main>
      </AnimatePresence>
      <Footer />
      <BackToTop />
    </>
  )
}

export default function App() {
  const location = useLocation()

  // PR2 Phase 6: every public route is duplicated across PREFIXED_LOCALES
  // (3 entries — zh-Hant / zh-Hans / en). Each locale-prefixed URL renders
  // the same page component; the locale itself is detected at runtime from
  // the URL pathname by `@/i18n`'s `localePath` detector, which calls
  // `parseLocaleFromPath` to pick the active i18n language.
  //
  // Why not a layout route with a `:locale` param? Two reasons:
  //   1. The bare-path form is gone (Phase 6 redirect map), so every URL
  //      has a locale prefix and a child route is enough.
  //   2. `:slug` already uses URL params; nesting `:locale/:rest*` would
  //      force every page to parse out the locale prefix manually.
  // A flat enumeration is the simplest correct structure.
  //
  // NOTE: `/pricing` is intentionally a bare redirect (not localised) —
  // it's a legacy alias, the destination `/products` is the canonical
  // landing page. The redirect target is in DEFAULT_LOCALE; the user can
  // re-pick locale after the redirect lands them on /zh-Hant/products.
  //
  // NOTE: `path: '*'` (NotFound) is the LAST route in each locale's set;
  // it catches `/zh-Hant/<unknown>` etc. and renders the localised 404.
  const BASE_ROUTES: { path: string; element: React.ReactElement }[] = [
    { path: '/', element: <HomePage /> },
    { path: '/about', element: <AboutPage /> },
    { path: '/products', element: <ProductsPage /> },
    { path: '/contact', element: <ContactPage /> },
    { path: '/magazine', element: <MagazinePage /> },
    { path: '/magazine/:slug', element: <MagazineDetailPage /> },
    { path: '/lectures', element: <LecturesPage /> },
    { path: '/lectures/:slug', element: <LectureDetailPage /> },
    { path: '/products/harbour', element: <HarbourPage /> },
    { path: '/products/values-incubator', element: <ValuesIncubatorPage /> },
    { path: '/products/smart-cindy', element: <SmartCindyPage /> },
    // 國民教育 — the KS2 / KS3 course pack. It used to be a hand-coded
    // standalone document (public/products/national-education/) with no
    // site chrome; it is a React route now, so it sits under PublicLayout
    // like every other product page and gains the navbar + footer.
    { path: '/products/national-education', element: <NationalEducationPage /> },
    // CSP — 升學及職涯起航平台. A React route with its own page stylesheet
    // (src/styles/csp.css), so it sits under PublicLayout like every other
    // product page and gains the navbar + footer.
    { path: '/products/csp', element: <CspPage /> },
    // The five products that previously had NO page of their own. Every
    // product card on /products now links to a real route (one React page
    // per slug, rendered from ProductDetailPage), so no card 404s.
    { path: '/products/hackathon', element: <ProductDetailPage slug="hackathon" /> },
    { path: '/products/exploration-trip', element: <ProductDetailPage slug="explorationTrip" /> },
    { path: '/products/mentorship', element: <ProductDetailPage slug="mentorship" /> },
    { path: '/products/flash-cards', element: <ProductDetailPage slug="flashCards" /> },
    { path: '/products/claw-machine', element: <ProductDetailPage slug="clawMachine" /> },
    // The sample-question page that lives in the same directory. It keeps
    // its `.html` extension because that URL is already published and
    // linked: `…/national-education/KS2-v3.html` is a FILE, not a
    // directory, and `…/KS2-v3.html/` is a 404.
    {
      path: '/products/national-education/KS2-v3.html',
      element: <NationalEducationKs2Page />,
    },
    { path: '/articles', element: <ArticleList /> },
    { path: '/articles/:slug', element: <ArticleDetail /> },
    { path: '/gallery', element: <GalleryListPage /> },
    { path: '/gallery/:slug', element: <GalleryDetailPage /> },
    // `*` must be the last entry — React Router matches in declaration order.
    { path: '*', element: <NotFoundPage /> },
  ]

  /**
   * Bare-path → localised-path redirects. Production nginx already does
   * 301 via the bare-path `location ~` block in `infra/nginx/aapanel.www.yada.hk.conf`,
   * but the dev server (`npm run dev`) has no nginx in front of
   * it, so React Router must handle these internally. Each bare path
   * gets a `<Navigate replace>` to its `/zh-Hant/` equivalent — same
   * behaviour the user sees in prod, just without the network round-trip.
   */
  const BARE_REDIRECTS: { from: string; to: string }[] = [
    { from: '/', to: '/zh-Hant/' },
    { from: '/about', to: '/zh-Hant/about' },
    { from: '/products', to: '/zh-Hant/products' },
    { from: '/products/harbour', to: '/zh-Hant/products/harbour' },
    { from: '/products/values-incubator', to: '/zh-Hant/products/values-incubator' },
    { from: '/products/smart-cindy', to: '/zh-Hant/products/smart-cindy' },
    { from: '/products/national-education', to: '/zh-Hant/products/national-education' },
    { from: '/products/csp', to: '/zh-Hant/products/csp' },
    { from: '/products/hackathon', to: '/zh-Hant/products/hackathon' },
    { from: '/products/exploration-trip', to: '/zh-Hant/products/exploration-trip' },
    { from: '/products/mentorship', to: '/zh-Hant/products/mentorship' },
    { from: '/products/flash-cards', to: '/zh-Hant/products/flash-cards' },
    { from: '/products/claw-machine', to: '/zh-Hant/products/claw-machine' },
    {
      from: '/products/national-education/KS2-v3.html',
      to: '/zh-Hant/products/national-education/KS2-v3.html',
    },
    { from: '/contact', to: '/zh-Hant/contact' },
    { from: '/magazine', to: '/zh-Hant/magazine' },
    { from: '/magazine/:slug', to: '/zh-Hant/magazine/:slug' },
    { from: '/lectures', to: '/zh-Hant/lectures' },
    { from: '/lectures/:slug', to: '/zh-Hant/lectures/:slug' },
    { from: '/articles', to: '/zh-Hant/articles' },
    { from: '/articles/:slug', to: '/zh-Hant/articles/:slug' },
    { from: '/gallery', to: '/zh-Hant/gallery' },
    { from: '/gallery/:slug', to: '/zh-Hant/gallery/:slug' },
    { from: '/pricing', to: '/zh-Hant/products' },
  ]

  return (
    <Routes location={location}>
      {/* Admin lives outside the public layout — its own chrome. Admin URLs
          are NOT localised; the CMS uses its own locale picker. */}
      <Route
        path="/admin/cms/*"
        element={
          <Suspense fallback={null}>
            <AdminApp />
          </Suspense>
        }
      />

      {/* Legacy bare-URL aliases. React Router matches in declaration
          order, so these routes take precedence over the localised ones
          below — a user typing `/about` in dev hits this entry and is
          redirected to `/zh-Hant/about` without going through nginx. In
          prod, nginx's 301 redirect map fires first (cheaper than a
          client-side navigate); this is defence-in-depth for the dev server
          and any user who types the legacy URL while browsing. */}
      {BARE_REDIRECTS.map((r) => (
        <Route
          key={`redirect:${r.from}`}
          path={r.from}
          element={<BareRedirect to={r.to} />}
        />
      ))}

      {/* Public site — 3 locales × 16 base paths = 48 route entries below
          (BASE_ROUTES includes the `*` catch-all, which also expands per
          locale). Each `key` includes the locale so React 19 can distinguish
          the same component mounted under three different paths. */}
      <Route element={<PublicLayout />}>
        {PREFIXED_LOCALES.flatMap((locale) =>
          BASE_ROUTES.map((r) => {
            // React Router requires `*` to follow a `/` in the pattern;
            // a localised `*` becomes `/{locale}/*` (catch everything
            // under the locale root) rather than `/{locale}*`.
            let localizedPath: string
            if (r.path === '/') {
              localizedPath = `/${locale}`
            } else if (r.path === '*') {
              localizedPath = `/${locale}/*`
            } else {
              localizedPath = `/${locale}${r.path}`
            }
            return (
              <Route
                key={`${locale}::${r.path}`}
                path={localizedPath}
                element={r.element}
              />
            )
          }),
        )}
      </Route>
    </Routes>
  )
}
